import { createSlice } from '@reduxjs/toolkit'
import _ from "lodash";
import { abbreviateDirection } from '../../app/common/util/util';

const initialState = {
  party: {},
  originalPartyId: null, // Track the original logged-in party when signing as a shared client
  partyInfoFromUrl: {},
  docsForViewing: {},
  docsForSigning: {},
  docsForParty: {},
  docUrl: "",
  doc: {},
  pageScale: 1.5,
  docToSign: {},
  docToView: {},
  tasksTrans: [],
  partiesForUser: {parties: [], column: "", direction: ""},
  transactionsForUser: [],
  docsNeedingSignature: [],
  sharedDocHistoryForUser: [],
  sharedDocsForUser: [],
}

const partySlice = createSlice({
  name: 'party',
  initialState,
  reducers: {
    fetchParty(state, action) {
      state.party = action.payload
    },
    fetchPartiesForUser(state, action) {
      if (state.partiesForUser.column && state.partiesForUser.direction) {
        state.partiesForUser.parties = _.orderBy(action.payload.parties, state.partiesForUser.column, abbreviateDirection(state.partiesForUser.direction))
      } else {
        state.partiesForUser.parties = action.payload.parties
      }
    },
    fetchTransactionsForUser(state, action) {
      state.transactionsForUser = action.payload
    },
    fetchDocsNeedingSignature(state, action) {
      state.docsNeedingSignature = action.payload
    },
    fetchSharedDocHistoryForUser(state, action) {
      state.sharedDocHistoryForUser = action.payload
    },
    fetchSharedDocsForUser(state, action) {
      state.sharedDocsForUser = action.payload
    },
    sortPartiesForUser(state, action) {
      const sortColumn = action.payload;

      // Custom sorting function for different data types
      const getSortValue = (party, column) => {
        switch (column) {
          case 'updatedAt':
          case 'closingDateTime':
            if (!party[column]) return new Date(0);
            return party[column].seconds ? new Date(party[column].seconds * 1000) : new Date(party[column]);
          case 'needsSignatures':
            return party[column] ? 1 : 0;
          case 'address':
            return party.address?.street || '';
          default:
            return party[column] || '';
        }
      };

      if (state.partiesForUser.column === sortColumn) {
        // Toggle direction if same column
        const newDirection = state.partiesForUser.direction === 'ascending' ? 'descending' : 'ascending';
        const sortedParties = state.partiesForUser.parties.slice().reverse();

        state.partiesForUser = {
          parties: sortedParties,
          column: sortColumn,
          direction: newDirection
        };
      } else {
        // Sort by new column
        const sortedParties = _.sortBy(state.partiesForUser.parties, (party) => getSortValue(party, sortColumn));

        state.partiesForUser = {
          parties: sortedParties,
          column: sortColumn,
          direction: 'ascending'
        };
      }
    },
    extractPartyInfoFromUrl(state, action) {
      state.partyInfoFromUrl = action.payload
    },
    fetchDocsForParty(state, action) {
      if (action.payload.docs) {
        action.payload.docs = _.sortBy(action.payload.docs, ["updatedAt"], ["descending"]).slice().reverse();
      }
      state.docsForParty = {docs: action.payload.docs}

      // Filter documents for signing - include docs where:
      // 1. This party's role has been sent for signing (in signingDetails with sentOut === true), AND
      // 2. This party has NOT yet signed (signed !== true)
      // Note: We check by role to distinguish between parties sharing the same email
      state.docsForSigning = {
        docs: action.payload.docs.filter(doc => {
          if (!doc.signingDetails) return false;

          // Find the signing detail for this party's specific role
          const signingDetail = Object.values(doc.signingDetails).find(detail =>
            detail.role === action.payload.role && detail.sentOut === true
          );

          // Only show if this party was sent for signing and hasn't signed yet
          return signingDetail && signingDetail.signed !== true;
        })
      }

      state.docsForViewing = {docs: action.payload.docs.filter(doc => !doc.signingRequestedFor || !doc.signingRequestedFor.includes(action.payload.email))}
    },
    fetchTasksForParty(state, action) {
      state.tasksTrans = action.payload.filter(task => task.archived !== true)
    },
    getDocAndUrl(state, action) {
      state.doc = action.payload.doc
			state.docUrl = action.payload.docUrl
		},
    changePageScale(state, action) {
      state.pageScale = action.payload
    },
		sortDocsForViewing(state, action) {
      if (state.docsForViewing.column === action.payload) {
        state.docsForViewing = {
          docs: state.docsForViewing.docs.slice().reverse(),
          column: action.payload,
          direction: state.docsForViewing.direction  === 'ascending' ? 'descending' : 'ascending'
        }
      } else {
        state.docsForViewing = {
          docs: _.sortBy(state.docsForViewing.docs, [action.payload]),
          column: action.payload,
          direction: 'ascending'
        }
      }
    },
    sortDocsForSigning(state, action) {
      if (state.docsForSigning.column === action.payload) {
        state.docsForSigning = {
          docs: state.docsForSigning.docs.slice().reverse(),
          column: action.payload,
          direction: state.docsForSigning.direction  === 'ascending' ? 'descending' : 'ascending'
        }
      } else {
        state.docsForSigning = {
          docs: _.sortBy(state.docsForSigning.docs, [action.payload]),
          column: action.payload,
          direction: 'ascending'
        }
      }
    },
    setDocToSign(state, action) {
      state.docToSign = action.payload
    },
    setDocToView(state, action) {
      state.docToView = action.payload
    },
    setOriginalPartyId(state, action) {
      state.originalPartyId = action.payload
    },
    clearOriginalPartyId(state, action) {
      state.originalPartyId = null
    },
  }
});

export const { fetchParty, fetchPartiesForUser, fetchTransactionsForUser, fetchDocsNeedingSignature, fetchSharedDocHistoryForUser, fetchSharedDocsForUser, sortPartiesForUser, extractPartyInfoFromUrl, fetchDocsForParty, fetchTasksForParty, getDocAndUrl, changePageScale, sortDocsForViewing, sortDocsForSigning, setDocToSign, setDocToView, setOriginalPartyId, clearOriginalPartyId } = partySlice.actions

export default partySlice.reducer